import { PageType } from '@app-types/PageType'
import SettingsModal from '@components/shared/SettingsModal/SettingsModal'
import DockSettingsHandle from './DockSettingsHandle'
import StopWatch from '@components/shared/Stopwatch/Stopwatch'
import classNames from 'classnames'
import { type FC, type FormEvent, type ReactNode, useEffect, useState } from 'react'
import {
  FaBookmark,
  FaChevronDown,
  FaClock,
  FaCompress,
  FaExpand,
  FaHeadphones,
  FaPause,
  FaRegBookmark,
  FaSliders,
} from 'react-icons/fa6'

export type ReaderBottomDockProps = {
  /** Whether the dock is docked in normal document flow after stats */
  isDocked?: boolean
  /** Whether an audio player is active/present on the page */
  hasAudio?: boolean
  /** Current page index */
  page: number
  /** Maximum page boundary */
  maxPage: number
  /** Minimum page boundary (default: 1) */
  minPage?: number
  /** Callback fired when user chooses a new page */
  onGoToPage: (_targetPage: number) => void
  /** Whether auto-scrolling is active */
  isScrolling: boolean
  /** Whether auto-scrolling is in its initial countdown/pending state */
  isPending: boolean
  /** Callback fired to toggle auto-scroll */
  onToggleScroll: () => void
  /** Whether current page has a saved reading bookmark */
  isPageMarked: boolean
  /** Callback fired to toggle page bookmark */
  onToggleBookmark: () => void
  /** Whether full-screen focus mode is active */
  isFocusMode: boolean
  /** Callback fired to toggle focus mode */
  onToggleFocusMode: () => void
  /** Type of the reading page */
  type: PageType
  /** Configured auto-scroll duration in seconds */
  autoScrollDuration?: number
  /** Setter for auto-scroll duration */
  setAutoScrollDuration?: (_duration: number) => void
  /** Optional quick actions to pass to SettingsModal */
  quickActions?: ReactNode
  /** Mounted audio panel; hiding it must not interrupt playback. */
  audioPanel?: ReactNode
  isAudioPanelOpen?: boolean
  isAudioPlaying?: boolean
  onToggleAudioPanel?: () => void
}

/**
 * Bottom capsule dock offering reading timer, auto-scroll,
 * page selector, bookmarking, full-screen focus, and reading settings.
 * Floats above viewport, or docks naturally after the stats section when reaching page bottom.
 */
const ReaderBottomDock: FC<ReaderBottomDockProps> = ({
  isDocked = false,
  hasAudio = false,
  page,
  maxPage,
  minPage = 1,
  onGoToPage,
  isScrolling,
  isPending,
  onToggleScroll,
  isPageMarked,
  onToggleBookmark,
  isFocusMode,
  onToggleFocusMode,
  type,
  autoScrollDuration,
  setAutoScrollDuration,
  quickActions,
  audioPanel,
  isAudioPanelOpen = false,
  isAudioPlaying = false,
  onToggleAudioPanel,
}) => {
  const [isPagePickerOpen, setIsPagePickerOpen] = useState(false)
  const [inputPage, setInputPage] = useState(String(page))

  useEffect(() => {
    setInputPage(String(page))
  }, [page])

  /** Validates the requested page before navigating. */
  const handlePageSubmit = (event: FormEvent) => {
    event.preventDefault()
    const targetPageNumber = Number(inputPage)
    if (
      inputPage.trim() !== '' &&
      Number.isInteger(targetPageNumber) &&
      targetPageNumber >= minPage &&
      targetPageNumber <= maxPage
    ) {
      onGoToPage(targetPageNumber)
      setIsPagePickerOpen(false)
    }
  }

  return (
    <SettingsModal
      type={type}
      autoScrollDuration={autoScrollDuration}
      setAutoScrollDuration={setAutoScrollDuration}
      quickActions={quickActions}
      trigger={(openModal) => (
        <aside
          aria-label="Okuma araç çubuğu"
          className={classNames(
            'flex flex-col items-center bg-white/95 dark:bg-[#0d1d29]/95 backdrop-blur-md border border-slate-300/80 dark:border-[#1e3445]/80 rounded-2xl sm:rounded-full px-3.5 sm:px-4 py-2 shadow-2xl transition-[background-color,border-color,box-shadow] duration-300',
            isFocusMode && audioPanel ? 'z-[60]' : 'z-30',
            Boolean(audioPanel) &&
              'w-[calc(100%-24px)] max-w-[390px] !rounded-[28px] !px-3',
            isDocked
              ? 'relative left-auto bottom-auto translate-x-0 mx-auto my-2 sm:my-3 max-w-fit'
              : classNames(
                  'fixed left-1/2 -translate-x-1/2',
                  audioPanel
                    ? 'bottom-[calc(12px+env(safe-area-inset-bottom))]'
                    : hasAudio
                      ? 'bottom-14 sm:bottom-15'
                      : 'bottom-4 sm:bottom-5',
                ),
          )}
        >
          <DockSettingsHandle onOpenSettings={openModal} />

          {audioPanel && (
            <div
              id="quran-audio-panel"
              hidden={!isAudioPanelOpen}
              className={classNames(
                'w-full border-b border-slate-200/80 pb-3 mb-2 dark:border-slate-700/60',
                isAudioPanelOpen &&
                  'motion-safe:animate-[fadeSlideIn_0.25s_ease-out_both]',
              )}
            >
              {audioPanel}
            </div>
          )}

          {/* Action Controls Row */}
          <div
            className={classNames(
              'flex items-center',
              audioPanel ? 'w-full justify-between gap-0.5' : 'gap-2 sm:gap-2.5',
            )}
          >
            {/* Reading Timer Pill */}
            <StopWatch
              width="auto"
              getTime={() => {}}
              className="!h-7 sm:!h-8 !px-2.5 sm:!px-3 !rounded-full !bg-teal-50 dark:!bg-[#072430] !border !border-teal-300 dark:!border-teal-500/30 !text-teal-700 dark:!text-teal-400 !font-mono !text-xs !font-bold flex items-center justify-center shrink-0 shadow-inner"
            />

            {/* Auto Scroll Toggle */}
            <button
              type="button"
              onClick={onToggleScroll}
              aria-label={
                isScrolling
                  ? 'Otomatik kaydırmayı durdur'
                  : isPending
                    ? 'Kaydırma başlıyor...'
                    : 'Otomatik kaydırmayı başlat'
              }
              title={
                isScrolling ? 'Otomatik kaydırmayı durdur' : 'Otomatik kaydırmayı başlat'
              }
              className={classNames(
                'w-8 h-8 flex items-center justify-center rounded-full transition-all',
                {
                  'bg-teal-500/20 text-teal-700 dark:text-teal-400 ring-1 ring-teal-500/40':
                    isScrolling,
                  'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/80':
                    !isScrolling,
                },
              )}
            >
              {isScrolling ? (
                <FaPause className="text-xs" />
              ) : isPending ? (
                <FaClock className="text-xs animate-pulse text-teal-700 dark:text-teal-400" />
              ) : (
                <FaChevronDown className="text-sm" />
              )}
            </button>

            {/* Page Indicator and Jump Picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsPagePickerOpen((prev) => !prev)}
                aria-label={`Sayfa ${page}, sayfayı değiştir`}
                title={`Sayfa ${page} (Tıkla ve değiştir)`}
                className="h-7 sm:h-8 px-3 rounded-full bg-slate-100 dark:bg-[#182a37] hover:bg-slate-200 dark:hover:bg-[#203646] text-slate-800 dark:text-white font-bold text-xs sm:text-sm border border-slate-200 dark:border-slate-700/60 flex items-center justify-center transition-colors"
              >
                {page}
              </button>
              {isPagePickerOpen && (
                <form
                  onSubmit={handlePageSubmit}
                  className="absolute bottom-11 left-1/2 -translate-x-1/2 bg-white dark:bg-[#0f1d28] border border-slate-200 dark:border-slate-700 rounded-xl p-2 shadow-xl flex items-center gap-1.5 z-50 min-w-[120px]"
                >
                  <input
                    type="number"
                    min={minPage}
                    max={maxPage}
                    value={inputPage}
                    onChange={(e) => setInputPage(e.target.value)}
                    className="w-14 h-7 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white text-center text-xs font-bold rounded border border-slate-300 dark:border-slate-600 focus:outline-hidden focus:border-teal-500"
                    aria-label="Gitmek istediğiniz sayfa"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-2.5 h-7 bg-teal-600 hover:bg-teal-500 text-white text-xs font-semibold rounded cursor-pointer"
                  >
                    Git
                  </button>
                </form>
              )}
            </div>

            {/* Subtle Vertical Divider */}
            <div
              aria-hidden="true"
              className="h-4 w-px bg-slate-300 dark:bg-slate-700/60 mx-0.5"
            />

            {/* Page Reading Bookmark */}
            <button
              type="button"
              onClick={onToggleBookmark}
              aria-label={isPageMarked ? 'Sayfa işaretini kaldır' : 'Sayfayı işaretle'}
              title={isPageMarked ? 'Sayfa işaretini kaldır' : 'Sayfayı işaretle'}
              className={classNames(
                'w-8 h-8 flex items-center justify-center rounded-full transition-colors',
                {
                  'text-teal-600 dark:text-teal-400': isPageMarked,
                  'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/80':
                    !isPageMarked,
                },
              )}
            >
              {isPageMarked ? (
                <FaBookmark className="text-sm" />
              ) : (
                <FaRegBookmark className="text-sm" />
              )}
            </button>

            {/* Full Screen Focus Mode Toggle */}
            <button
              type="button"
              onClick={onToggleFocusMode}
              aria-label={isFocusMode ? 'Odak modundan çık' : 'Tam ekran okuma'}
              title={isFocusMode ? 'Odak modundan çık' : 'Tam ekran okuma'}
              className={classNames(
                'w-8 h-8 flex items-center justify-center rounded-full transition-colors',
                {
                  'text-teal-600 dark:text-teal-400': isFocusMode,
                  'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/80':
                    !isFocusMode,
                },
              )}
            >
              {isFocusMode ? (
                <FaCompress className="text-sm" />
              ) : (
                <FaExpand className="text-sm" />
              )}
            </button>

            {audioPanel && (
              <button
                type="button"
                onClick={onToggleAudioPanel}
                aria-label={isAudioPanelOpen ? 'Ses panelini kapat' : 'Ses panelini aç'}
                aria-expanded={isAudioPanelOpen}
                aria-controls="quran-audio-panel"
                title="Kur’an dinle"
                className={classNames(
                  'relative w-8 h-8 flex shrink-0 items-center justify-center rounded-full transition-colors',
                  isAudioPanelOpen || isAudioPlaying
                    ? 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800/80',
                )}
              >
                <FaHeadphones className="text-sm" />
                {isAudioPlaying && (
                  <span
                    aria-label="Ses çalıyor"
                    className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-teal-500"
                  />
                )}
              </button>
            )}

            {/* Both the handle and settings icon open the same panel. */}
            <button
              type="button"
              onClick={openModal}
              aria-label="Okuma ayarlarını aç"
              title="Okuma ayarları"
              className="w-8 h-8 flex items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/80 transition-colors"
            >
              <FaSliders className="text-sm" />
            </button>
          </div>
        </aside>
      )}
    />
  )
}

export default ReaderBottomDock
